import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Heart, MessageCircle, Clock, User, Building, Edit, Trash2, MoreVertical } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { formatDistanceToNow, parseISO, format } from 'date-fns';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForumPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'post' | 'reply', id: string }

  useEffect(() => {
    fetchPost();
  }, [id]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/forum/posts/${id}`, { headers });
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching post:', error);
      if (error.response?.status === 404) {
        toast.error('Post not found');
        navigate('/forum');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (replyId = null) => {
    if (!user) {
      toast.error('Please log in to like posts');
      navigate('/login');
      return;
    }

    try {
      const url = replyId 
        ? `${API}/forum/posts/${id}/like?reply_id=${replyId}`
        : `${API}/forum/posts/${id}/like`;
      
      await axios.post(url, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPost();
    } catch (error) {
      toast.error('Error liking post');
    }
  };

  const handleReply = async () => {
    if (!user) {
      toast.error('Please log in to reply');
      navigate('/login');
      return;
    }

    if (!replyContent.trim()) {
      toast.error('Please enter a reply');
      return;
    }

    setSubmittingReply(true);
    try {
      await axios.post(`${API}/forum/posts/${id}/replies`, 
        { content: replyContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reply posted!');
      setReplyContent('');
      fetchPost();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error posting reply');
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleEdit = async () => {
    try {
      await axios.put(`${API}/forum/posts/${id}`, 
        { content: editContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Post updated!');
      setEditMode(false);
      fetchPost();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating post');
    }
  };

  const handleDelete = async () => {
    try {
      if (deleteTarget.type === 'post') {
        await axios.delete(`${API}/forum/posts/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Post deleted');
        navigate('/forum');
      } else {
        await axios.delete(`${API}/forum/replies/${deleteTarget.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Reply deleted');
        fetchPost();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting');
    } finally {
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    }
  };

  const getCategoryLabel = (value) => {
    const categories = {
      general: 'General Discussion',
      home_care: 'Home Care',
      senior_living: 'Senior Living',
      job_board: 'Job Board',
      events: 'Events & Networking',
      resources: 'Resources & Tips',
    };
    return categories[value] || value;
  };

  const getCategoryColor = (value) => {
    const colors = {
      general: 'bg-secondary text-secondary-foreground',
      home_care: 'bg-blue-500/20 text-blue-400',
      senior_living: 'bg-green-500/20 text-green-400',
      job_board: 'bg-purple-500/20 text-purple-400',
      events: 'bg-gold/20 text-gold',
      resources: 'bg-orange-500/20 text-orange-400',
    };
    return colors[value] || 'bg-secondary text-secondary-foreground';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!post) {
    return null;
  }

  const isAuthor = user && user.id === post.author_id;
  const isAdmin = user && user.role === 'admin';

  return (
    <div className="min-h-screen py-12">
      <div className="container-custom max-w-4xl mx-auto">
        {/* Back Link */}
        <Link 
          to="/forum" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-gold mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Forum
        </Link>

        {/* Post */}
        <article className="border border-border/50 bg-card p-6 md:p-8 mb-8">
          <div className="flex items-start justify-between mb-4">
            <span className={`px-3 py-1 text-xs uppercase tracking-wider rounded ${getCategoryColor(post.category)}`}>
              {getCategoryLabel(post.category)}
            </span>
            {(isAuthor || isAdmin) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => {
                    setEditContent(post.content);
                    setEditMode(true);
                  }}>
                    <Edit className="w-4 h-4 mr-2" /> Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="text-destructive"
                    onClick={() => {
                      setDeleteTarget({ type: 'post', id: post.id });
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <h1 className="text-2xl md:text-3xl mb-4">{post.title}</h1>

          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6 pb-6 border-b border-border/50">
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {post.author_name}
            </span>
            {post.author_company_type && (
              <span className="flex items-center gap-1">
                <Building className="w-4 h-4" />
                {post.author_company_type}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {format(parseISO(post.created_at), 'MMM d, yyyy h:mm a')}
            </span>
          </div>

          {editMode ? (
            <div className="space-y-4">
              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                rows={8}
                className="rounded-sm"
              />
              <div className="flex gap-2">
                <Button onClick={handleEdit} className="bg-gold text-primary hover:bg-gold/90">
                  Save Changes
                </Button>
                <Button variant="outline" onClick={() => setEditMode(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="prose prose-invert max-w-none mb-6">
              <p className="whitespace-pre-wrap">{post.content}</p>
            </div>
          )}

          <div className="flex items-center gap-4 pt-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleLike()}
              className={post.user_liked ? 'text-red-500' : ''}
              data-testid="like-post-btn"
            >
              <Heart className={`w-4 h-4 mr-1 ${post.user_liked ? 'fill-current' : ''}`} />
              {post.like_count}
            </Button>
            <span className="flex items-center gap-1 text-sm text-muted-foreground">
              <MessageCircle className="w-4 h-4" />
              {post.reply_count} replies
            </span>
          </div>
        </article>

        {/* Replies Section */}
        <div className="space-y-6">
          <h2 className="text-xl flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-gold" />
            Replies ({post.replies?.length || 0})
          </h2>

          {/* Reply Form */}
          {user ? (
            <div className="border border-border/50 bg-card p-4">
              <Textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="Write a reply..."
                rows={4}
                className="rounded-sm mb-3"
                data-testid="reply-input"
              />
              <Button 
                onClick={handleReply}
                disabled={submittingReply}
                className="bg-gold text-primary hover:bg-gold/90"
                data-testid="submit-reply-btn"
              >
                {submittingReply ? 'Posting...' : 'Post Reply'}
              </Button>
            </div>
          ) : (
            <div className="border border-border/50 bg-card p-4 text-center">
              <p className="text-muted-foreground mb-2">Log in to reply to this post</p>
              <Button onClick={() => navigate('/login')} variant="outline">
                Log In
              </Button>
            </div>
          )}

          {/* Replies List */}
          {post.replies?.map((reply) => (
            <div key={reply.id} className="border border-border/50 bg-card p-4" data-testid={`reply-${reply.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {reply.author_name}
                  </span>
                  {reply.author_company_type && (
                    <span className="flex items-center gap-1">
                      <Building className="w-4 h-4" />
                      {reply.author_company_type}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {formatDistanceToNow(parseISO(reply.created_at), { addSuffix: true })}
                  </span>
                </div>
                {(user?.id === reply.author_id || isAdmin) && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setDeleteTarget({ type: 'reply', id: reply.id });
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                  </Button>
                )}
              </div>
              <p className="whitespace-pre-wrap mb-3">{reply.content}</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleLike(reply.id)}
                data-testid={`like-reply-${reply.id}`}
              >
                <Heart className="w-4 h-4 mr-1" />
                {reply.like_count}
              </Button>
            </div>
          ))}

          {post.replies?.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No replies yet. Be the first to reply!
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleteTarget?.type === 'post' ? 'Post' : 'Reply'}</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this {deleteTarget?.type}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ForumPost;
